"use client";

import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { FaApple, FaGooglePlay, FaArrowRight } from 'react-icons/fa';

import styles from './HomePage.module.css';

import HeroVisualDemo    from '../IphoneVisualDemo/HeroVisualDemo';
import CommunityPods     from '../CommunityPods/CommunityPods';
import FeaturesSection   from '../FeatureSection/FeaturesSection';
import SocialCreditGraph from '../SocialCreditGraph/SocialCreditGraph';
import P2PLending        from '../P2PLending/P2PLending';
import ServicesSection   from '../ServiceSection/ServicesSection';
import TestimonialSlider from '../Testimonials/TestimonialSlider';

/* ── Deterministic particles (no hydration mismatch) ── */
const PARTICLES = Array.from({ length: 24 }, (_, i) => ({
  left:     `${(i * 47 + 13) % 100}%`,
  top:      `${(i * 31 +  7) % 100}%`,
  delay:    `${((i * 1.7) % 24).toFixed(1)}s`,
  color:    i % 3 === 0 ? 'var(--teal)'
          : i % 3 === 1 ? 'var(--gold)'
          : 'var(--coral)',
}));

/* ── SEO schemas — outside component to avoid recreation ── */
const structuredData = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Rupayya",
  "alternateName": "India's Social Credit Infrastructure",
  "url": "https://rupayya.com",
  "description": "Making lending between real connections secure and digital.",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://rupayya.com/search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  },
  "inLanguage": "en-IN"
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Rupayya",
  "url": "https://rupayya.com",
  "logo": "https://rupayya.com/logo.png",
  "sameAs": [
    "https://facebook.com/rupayya",
    "https://twitter.com/rupayya",
    "https://instagram.com/rupayya",
    "https://linkedin.com/company/rupayya"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+91-XXXXXXXXXX",
    "contactType": "customer service",
    "areaServed": "IN",
    "availableLanguage": ["en", "hi"]
  }
};

const financialServiceSchema = {
  "@context": "https://schema.org",
  "@type": "FinancialService",
  "name": "Rupayya P2P Lending Platform",
  "image": "https://rupayya.com/og-image.jpg",
  "description": "India's Social Credit Infrastructure for secure digital lending between friends and family",
  "url": "https://rupayya.com",
  "telephone": "+91-XXXXXXXXXX",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Mumbai",
    "addressRegion": "MH",
    "addressCountry": "IN"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "1250"
  }
};

/* ── Stagger animation variants ── */
const fadeUp = {
  hidden:  { opacity: 0, y: 28 },
  visible: (delay = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.65, delay, ease: [0.4, 0, 0.2, 1] },
  }),
};

/* ════════════════════════════════════════════════════════
   HOMEPAGE COMPONENT
════════════════════════════════════════════════════════ */
const HomePage = () => {
  const [mobileNumber,    setMobileNumber]    = useState('');
  const [formSubmitted,   setFormSubmitted]   = useState(false);
  const [isLandscape,     setIsLandscape]     = useState(false);
  const [pastFold,        setPastFold]        = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const heroRef     = useRef(null);
  const featuresRef = useRef(null);

  const heroInView     = useInView(heroRef,     { once: true });
  const featuresInView = useInView(featuresRef, { once: true });

  useEffect(() => {
    const onScroll = () => {
      const past = window.scrollY > 500;
      setPastFold(past);
      if (!past) setBannerDismissed(false);
    };
    const onResize = () => setIsLandscape(window.innerWidth > window.innerHeight);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    onResize();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mobileNumber.length === 10) {
      setFormSubmitted(true);
      setTimeout(() => {
        alert(`Thank you! Download link sent to +91 ${mobileNumber}`);
        setMobileNumber('');
        setFormSubmitted(false);
      }, 1000);
    } else {
      alert('Please enter a valid 10-digit mobile number');
    }
  };

  const bannerVisible = pastFold && !bannerDismissed;

  return (
    <div className={styles.overflowGuard}>
      <div className={styles.homePage}>

        {/* ── SEO ── */}
        <Helmet>
          <title>Rupayya – India's First Social Credit Infrastructure for Secure P2P Lending</title>
          <meta name="description" content="Transform your social connections into financial opportunities. Rupayya is India's revolutionary social credit infrastructure for secure P2P lending." />
          <meta name="keywords"    content="P2P lending India, social credit score, digital lending app, friend loans, personal loans India, secure lending platform" />
          <link rel="canonical" href="https://rupayya.com" />
          <meta property="og:title"       content="Rupayya – India's Social Credit Infrastructure" />
          <meta property="og:description" content="Making lending between real connections secure and digital." />
          <meta property="og:image"       content="https://rupayya.com/og-image.jpg" />
          <meta property="og:url"         content="https://rupayya.com" />
          <meta property="og:type"        content="website" />
          <meta property="og:site_name"   content="Rupayya" />
          <meta property="og:locale"      content="en_IN" />
          <meta name="twitter:card"        content="summary_large_image" />
          <meta name="twitter:title"       content="Rupayya – India's Social Credit Infrastructure" />
          <meta name="twitter:description" content="Secure P2P lending between trusted connections." />
          <meta name="twitter:image"       content="https://rupayya.com/twitter-image.jpg" />
          <meta name="robots"    content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
          <meta name="googlebot" content="index, follow" />
          <meta name="theme-color" content="#00D4AA" />
          <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
          <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
          <script type="application/ld+json">{JSON.stringify(financialServiceSchema)}</script>
        </Helmet>

        {/* ── Ambient background particles ── */}
        <div className={styles.animatedBgElements} aria-hidden="true">
          {PARTICLES.map((p, i) => (
            <div
              key={i}
              className={styles.bgParticle}
              style={{
                left:            p.left,
                top:             p.top,
                animationDelay:  p.delay,
                backgroundColor: p.color,
              }}
            />
          ))}
        </div>

        {/* ════════════════════════════════
            HERO SECTION
        ════════════════════════════════ */}
        <section
          className={`${styles.heroSection} ${isLandscape ? styles.landscapeMode : ''}`}
          id="hero"
          ref={heroRef}
        >
          <div className={styles.heroInner}>

            {/* ── Text + CTA column ── */}
            <div className={styles.heroContent}>

              {/* Eyebrow tag */}
              <motion.div
                className={styles.cursiveTagline}
                variants={fadeUp}
                initial="hidden"
                animate={heroInView ? 'visible' : 'hidden'}
                custom={0}
              >
                <span className={styles.cursiveText}>
                  India's First Social Lending Infrastructure
                </span>
              </motion.div>

              {/* H1 */}
              <motion.h1
                className={styles.heroTitle}
                variants={fadeUp}
                initial="hidden"
                animate={heroInView ? 'visible' : 'hidden'}
                custom={0.1}
              >
                Lending between{' '}
                <span className={styles.highlight}>real connections</span>
                ,{' '}made secure
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                className={styles.heroSubtitle}
                variants={fadeUp}
                initial="hidden"
                animate={heroInView ? 'visible' : 'hidden'}
                custom={0.2}
              >
                <span style={{ display: 'block' }}>A financial identity built on trust, relationships, and real-life repayment behaviour.</span>
                <span style={{ display: 'block' }}>Transform your social network into financial opportunities.</span>
              </motion.p>

              {/* CTA form */}
              <motion.div
                className={styles.heroCta}
                variants={fadeUp}
                initial="hidden"
                animate={heroInView ? 'visible' : 'hidden'}
                custom={0.35}
              >
                <form onSubmit={handleSubmit} className={styles.ctaCard} noValidate>
                  <div className={styles.ctaInputRow}>
                    <div className={styles.countryCode} aria-label="Country code India (+91)">
                      +91
                    </div>
                    <input
                      className={styles.phoneInput}
                      type="tel"
                      value={mobileNumber}
                      onChange={(e) =>
                        setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))
                      }
                      placeholder="Enter mobile number"
                      required
                      pattern="[0-9]{10}"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      aria-label="Mobile number"
                    />
                  </div>
                  <motion.button
                    type="submit"
                    className={styles.submitBtn}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    disabled={formSubmitted}
                  >
                    {formSubmitted ? 'Sending…' : 'Get App Link'}
                    <FaArrowRight aria-hidden="true" />
                  </motion.button>
                </form>
              </motion.div>

            </div>{/* /heroContent */}

            {/* ── Phone visual ── */}
            <div className={styles.heroVisual}>
              <HeroVisualDemo heroInView={heroInView} isLandscape={isLandscape} />
            </div>

          </div>{/* /heroInner */}
        </section>

        {/* ── Content sections (unchanged) ── */}
        <FeaturesSection featuresRef={featuresRef} featuresInView={featuresInView} />
        <CommunityPods />
        <SocialCreditGraph />
        <P2PLending />
        <ServicesSection />
        <TestimonialSlider />

        {/* ── Fixed download banner ── */}
        <motion.div
          className={styles.ctaBanner}
          initial={{ y: '100%' }}
          animate={{ y: bannerVisible ? '0%' : '100%' }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          aria-hidden={!bannerVisible}
          role="region"
          aria-label="App download banner"
        >
          <div className={styles.bannerContent}>
            <h4>Start your financial journey today</h4>
            <p>Download Rupayya and join thousands of satisfied users</p>
          </div>
          <div className={styles.bannerActions}>
            <button className={styles.storeBtn} aria-label="Download on App Store">
              <FaApple aria-hidden="true" /> App Store
            </button>
            <button className={styles.storeBtn} aria-label="Download on Google Play">
              <FaGooglePlay aria-hidden="true" /> Play Store
            </button>
            <button
              className={styles.closeBanner}
              onClick={() => setBannerDismissed(true)}
              aria-label="Close banner"
            >
              ×
            </button>
          </div>
        </motion.div>

      </div>{/* /homePage */}
    </div>
  );
};

export default HomePage;